import { useState, useCallback } from 'react';
import { useReceptionStrings } from '../lib/receptionStrings';
import { useBookingClinics } from './useDoctorBooking';

// ─────────────────────────────────────────────────────────────────────────────
// پذیرش's half of «ثبت نوبت» and «لغو درخواست» — ONE hook for both surfaces, so
// the web day sheet and the phone list cannot disagree about when the doctor may
// book or what a booking says afterwards.
//
// Feedback (DESIGN_CONCEPT §2.4): closing the overlay IS the feedback when the
// new row is on screen. A toast is added only when it is not — the نوبت is on
// another day, or it went to the clinic for approval (the row appears greyed,
// and «در انتظار تأیید کلینیک» is news the doctor has to read once).
// ─────────────────────────────────────────────────────────────────────────────

export function useReceptionBooking({ selISO, reload, withdrawRequest, toast }) {
  const s = useReceptionStrings();
  const booking = useBookingClinics();
  const canBook = (booking.clinics || []).length > 0;

  const [bookOpen, setBookOpen] = useState(false);
  const [pendingWithdraw, setPendingWithdraw] = useState(null);
  // «تغییر زمان» (PM-0102): the row being moved, or null.
  const [rescheduling, setRescheduling] = useState(null);

  // The same forecast «ثبت نوبت» makes, for a move: a نوبت still waiting for its
  // patient, at a clinic that lets this doctor book directly (a نوبت that names
  // no clinic is the doctor's own diary). Where the clinic withdrew the grant the
  // move is the desk's, so no button is offered. Display only — the BFF decides
  // again from the membership row when it writes.
  const canReschedule = useCallback((apt) => {
    if (!apt?.id || apt.status !== 'scheduled' || !apt.dateTime) return false;
    if (!apt.clinicId) return true;
    return Boolean((booking.clinics || []).find((c) => c.clinicId === apt.clinicId)?.canBookDirectly);
  }, [booking.clinics]);

  // The row is on screen when it stayed on this day: its new hour IS the
  // feedback. Moved to another day it leaves the list, so that is said in words.
  const onRescheduled = useCallback((_moved, { dayISO, when } = {}) => {
    reload?.();
    if (dayISO && dayISO !== selISO) toast?.success?.(s('dashboard.reschedule.moved', { when: when || '' }));
  }, [reload, toast, s, selISO]);

  const onBooked = useCallback((result, { dayISO, walkIn } = {}) => {
    reload?.();
    // A walk-in has no hour to find on the list, so it is always confirmed in words.
    if (walkIn) toast?.success?.(s('dashboard.book.walkInBooked'));
    else if (result?.outcome === 'requested') toast?.success?.(s('dashboard.book.requested'));
    else if (dayISO && dayISO !== selISO) toast?.success?.(s('dashboard.book.booked'));
  }, [reload, toast, s, selISO]);

  // Returned to the ConfirmDialog so it stays open until the write settles.
  const confirmWithdraw = useCallback(async () => {
    if (!pendingWithdraw) return;
    try {
      await withdrawRequest(pendingWithdraw.id);
      setPendingWithdraw(null);
    } catch {
      setPendingWithdraw(null);
      toast?.error?.(s('dashboard.withdraw.failed'));
    }
  }, [pendingWithdraw, withdrawRequest, toast, s]);

  return {
    s,
    canBook,
    bookingClinics: booking,
    bookOpen,
    openBook: useCallback(() => setBookOpen(true), []),
    closeBook: useCallback(() => setBookOpen(false), []),
    onBooked,
    pendingWithdraw,
    askWithdraw: setPendingWithdraw,
    cancelWithdraw: useCallback(() => setPendingWithdraw(null), []),
    confirmWithdraw,
    canReschedule,
    rescheduling,
    askReschedule: setRescheduling,
    closeReschedule: useCallback(() => setRescheduling(null), []),
    onRescheduled,
  };
}

export default useReceptionBooking;
